import { isCalendarBusinessTrashed } from "../utils/businessTrash";
// LOOP CALENDAR SELECTOR FAST STABLE V22 — cache-first HTTP source of truth for owner + worker calendars.
// LOOP AGENDA MADRE WORKER ACCESS MIRROR V86 — worker business rows merge mirror worker profile instead of dropping it.
/**
 * src/services/userBusinesses.js
 *
 * Finds businesses related to the current user:
 * - owner/admin/manager
 * - worker/employee by uid or email in businesses/{businessId}/employees
 *
 * Important:
 * This file intentionally supports many field names because older data may use:
 * email, employeeEmail, workerEmail, userUid, employeeUserUid, etc.
 */

import {
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";

import { db as defaultDb, auth as defaultAuth } from "../firebase";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor, httpRequestJson } from "../utils/nativeHttp";
import { getManualAuth, isManualAuthLikelyValid } from "../utils/manualAuth";
import { makeUserScopedStorageKey, readJsonStorage, writeJsonStorage } from "../auth/authStorage";

const CACHE_KEY_PREFIX = "loop_user_businesses_roles_v7_identity";

const LOOP_USER_BUSINESSES_HTTP_URL = "https://us-central1-web-app-d9799.cloudfunctions.net/listUserBusinessesHttp";

function assertCurrentBusinessIdentity(user) {
  const current = isIosCapacitor() ? getManualAuth() : defaultAuth?.currentUser;
  if (!user?.uid || !current?.uid || String(current.uid) !== String(user.uid)) {
    throw new Error("user-businesses-session-changed");
  }
  return current;
}
async function getUserBusinessesHttpIdTokenV22(user) {
  const current = assertCurrentBusinessIdentity(user);
  const token = isIosCapacitor()
    ? (isManualAuthLikelyValid(current) ? current.idToken : "")
    : await current.getIdToken(false);
  assertCurrentBusinessIdentity(user);
  return token || "";
}

function normalizeHttpBusinessRowV22(item = {}) {
  const source = item && typeof item === "object" ? item : {};
  const business = source.business && typeof source.business === "object" ? source.business : {};
  const businessId = String(source.businessId || source.id || business.id || "").trim();
  const role = String(source.role || "owner").toLowerCase() === "worker" ? "worker" : "owner";
  const businessName = businessNameFrom({ ...business, ...source, id: businessId, businessId }, businessId);

  return {
    ...source,
    id: businessId,
    businessId,
    businessName,
    name: businessName || source.name || source.businessName || "",
    businessLogoUrl: source.businessLogoUrl || source.businessLogo || source.logoUrl || business.logoUrl || business.businessLogoUrl || business.logo || "",
    businessLogo: source.businessLogo || source.businessLogoUrl || source.logoUrl || business.businessLogo || business.logoUrl || business.logo || "",
    logoUrl: source.logoUrl || source.businessLogoUrl || source.businessLogo || business.logoUrl || business.businessLogoUrl || business.logo || "",
    role,
    employeeId: source.employeeId || source.workerId || source.employee?.id || null,
    employeeEmail: norm(source.employeeEmail || source.workerEmail || source.email || source.employee?.email || "") || null,
    workerName: source.workerName || source.employeeName || source.employee?.name || source.employee?.displayName || "",
    employeeName: source.employeeName || source.workerName || source.employee?.name || source.employee?.displayName || "",
    workerPhotoUrl: source.workerPhotoUrl || source.employeePhotoUrl || source.photoURL || source.photoUrl || source.employee?.photoURL || source.employee?.photoUrl || "",
    employeePhotoUrl: source.employeePhotoUrl || source.workerPhotoUrl || source.photoURL || source.photoUrl || source.employee?.photoURL || source.employee?.photoUrl || "",
    calendarSettings: source.calendarSettings || source.employee?.calendarSettings || null,
    availability: source.availability || source.schedule || source.workingHours || source.employee?.availability || source.employee?.schedule || null,
    schedule: source.schedule || source.availability || source.workingHours || source.employee?.schedule || source.employee?.availability || null,
    workingHours: source.workingHours || source.availability || source.schedule || source.employee?.workingHours || source.employee?.availability || null,
    raw: source.raw || source,
  };
}

function mergeUserBusinessRowsV22(items = []) {
  const map = new Map();
  for (const row of Array.isArray(items) ? items : []) {
    const normalized = normalizeHttpBusinessRowV22(row);
    if (!normalized.businessId) continue;
    const key = `${normalized.role}:${normalized.businessId}`;
    const prev = map.get(key) || {};
    map.set(key, {
      ...prev,
      ...normalized,
      businessId: normalized.businessId || prev.businessId,
      id: normalized.businessId || prev.id,
      businessName: cleanBusinessDisplayName(normalized.businessName, normalized.businessId) || cleanBusinessDisplayName(prev.businessName, prev.businessId) || null,
      name: cleanBusinessDisplayName(normalized.businessName, normalized.businessId) || cleanBusinessDisplayName(prev.businessName, prev.businessId) || null,
      businessLogoUrl: normalized.businessLogoUrl || prev.businessLogoUrl || prev.businessLogo || prev.logoUrl || "",
      businessLogo: normalized.businessLogo || prev.businessLogo || normalized.businessLogoUrl || prev.businessLogoUrl || "",
      logoUrl: normalized.logoUrl || prev.logoUrl || normalized.businessLogoUrl || prev.businessLogoUrl || "",
      role: normalized.role || prev.role || "owner",
      employeeId: normalized.employeeId || prev.employeeId || null,
      employeeEmail: normalized.employeeEmail || prev.employeeEmail || null,
      workerName: normalized.workerName || prev.workerName || normalized.employeeName || prev.employeeName || "",
      employeeName: normalized.employeeName || prev.employeeName || normalized.workerName || prev.workerName || "",
      workerPhotoUrl: normalized.workerPhotoUrl || prev.workerPhotoUrl || normalized.employeePhotoUrl || prev.employeePhotoUrl || "",
      employeePhotoUrl: normalized.employeePhotoUrl || prev.employeePhotoUrl || normalized.workerPhotoUrl || prev.workerPhotoUrl || "",
      calendarSettings: normalized.calendarSettings || prev.calendarSettings || null,
      availability: normalized.availability || prev.availability || null,
      schedule: normalized.schedule || prev.schedule || normalized.availability || prev.availability || null,
      workingHours: normalized.workingHours || prev.workingHours || normalized.availability || prev.availability || null,
      raw: { ...(prev.raw && typeof prev.raw === "object" ? prev.raw : {}), ...(normalized.raw && typeof normalized.raw === "object" ? normalized.raw : {}) },
    });
  }

  return Array.from(map.values()).filter(row => !isCalendarBusinessTrashed(row)).sort((a, b) => {
    if (a.role === b.role) return String(a.businessName || "").localeCompare(String(b.businessName || ""));
    return a.role === "owner" ? -1 : 1;
  });
}

async function fetchUserBusinessesViaHttpV22(user, options = {}, db = defaultDb) {
  const { uid, email } = getCurrentIdentity(user);
  const token = await getUserBusinessesHttpIdTokenV22(user);
  if (!token) throw new Error("user-businesses-auth-not-ready");

  const response = await withTimeout(
    httpRequestJson({
      url: LOOP_USER_BUSINESSES_HTTP_URL,
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      data: {
        uid: uid || null,
        email: email || null,
        includeWorkerProfiles: true,
        source: "userBusinesses_v22",
      },
    }),
    Number(options.httpTimeoutMs || (isIosCapacitor() ? 5200 : 4200)),
    "user-businesses-http-timeout-v22"
  );

  if (response?.ok === false || (!Array.isArray(response?.businesses) && !Array.isArray(response?.items))) {
    throw new Error("user-businesses-invalid-response");
  }
  const rows = Array.isArray(response?.businesses) ? response.businesses : response.items;
  // Older deployed endpoints omit private businesses. Recover only ownership
  // proven by authenticated, rules-enforced queries; never infer worker access.
  const owned = response.version === 'v23-management-visibility'
    ? [] : await withTimeout(listOwnedBusinesses(db, { uid, email }), 4500, 'owner-list-timeout')
      .catch(error => {
        if (!rows.length) throw error;
        warnOnce('owner-list-compatibility', '[userBusinesses] owner lookup unavailable', error?.message);
        return [];
      });
  return mergeUserBusinessRowsV22([...rows, ...owned.map(business => ({
    ...business, businessId: business.id, role: 'owner', raw: business,
  }))]);
}

export async function listOwnedBusinesses(db, { uid, email } = {}) {
  const checks = [];
  const read = async (field, value) => {
    if (isIosCapacitor()) return fsClient.listCollectionByPath('businesses', {
      where: [{ field, op: 'EQUAL', value }],
    }, { db });
    const snap = await getDocs(query(collection(db, 'businesses'), where(field, '==', value)));
    return snap.docs.map(d => ({ ...d.data(), id: d.id }));
  };
  if (uid) checks.push(read('ownerUid', uid));
  if (email) checks.push(read('ownerEmail', norm(email)));
  const settled = await Promise.allSettled(checks);
  if (settled.length && settled.every(result => result.status === 'rejected')) {
    throw settled[0].reason;
  }
  const rows = new Map();
  settled.forEach(result => {
    if (result.status === 'fulfilled') (result.value || []).forEach(row => rows.set(row.id, row));
  });
  return [...rows.values()].filter(row => !isCalendarBusinessTrashed(row));
}


// LOOP AGENDA BULLETPROOF: avoid console floods when Firestore rules intentionally
// block optional fallback scans. Keep one useful warning per unique path/message.
const loopUserBusinessWarned = new Set();
function warnOnce(key, ...args) {
  const safeKey = String(key || "warning");
  if (loopUserBusinessWarned.has(safeKey)) return;
  loopUserBusinessWarned.add(safeKey);
  // eslint-disable-next-line no-console
  console.warn(...args);
}


function norm(value) {
  return String(value || "").trim().toLowerCase();
}

function uniqueTruthy(list) {
  return Array.from(new Set((list || []).filter(Boolean).map(String)));
}

function withTimeout(promise, timeoutMs = 4500, label = "timeout") {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(label)), timeoutMs);
    }),
  ]).finally(() => {
    try { if (timer) clearTimeout(timer); } catch (_) {}
  });
}

function hasAnyRole(items) {
  return Array.isArray(items) && items.some((item) => item && item.businessId);
}

function getCurrentIdentity(user) {
  let uid = user?.uid ? String(user.uid) : "";
  let email = norm(user?.email);

  if ((!uid || !email) && isIosCapacitor()) {
    try {
      const manualAuth = getManualAuth();
      if (manualAuth && isManualAuthLikelyValid(manualAuth)) {
        if (!uid && manualAuth.uid) uid = String(manualAuth.uid);
        if (!email && manualAuth.email && (!uid || uid === String(manualAuth.uid))) email = norm(manualAuth.email);
      }
    } catch (_) {}
  }

  return { uid, email };
}

function cacheKeyForUser(user) {
  const { uid, email } = getCurrentIdentity(user);
  return makeUserScopedStorageKey(CACHE_KEY_PREFIX, { uid, email });
}

export function getCachedUserBusinesses(user) {
  try {
    const parsed = readJsonStorage(cacheKeyForUser(user), []);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function setCachedUserBusinesses(user, items) {
  try {
    writeJsonStorage(cacheKeyForUser(user), Array.isArray(items) ? items : []);
  } catch (_) {}
}

function hasUidInField(fieldValue, uid) {
  if (!uid || !fieldValue) return false;

  if (Array.isArray(fieldValue)) {
    return fieldValue.map(String).includes(String(uid));
  }

  if (typeof fieldValue === "object") {
    return Boolean(fieldValue[String(uid)]);
  }

  return String(fieldValue) === String(uid);
}

// LOOP CALENDAR SELECTOR NAME GUARD V61
// Never use a Firestore document id as a visible business name.
function looksLikeFirestoreBusinessId(value = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  return /^[A-Za-z0-9_-]{16,}$/.test(text) && !/[\sñÑáéíóúÁÉÍÓÚ#&.]/.test(text);
}

function cleanBusinessDisplayName(value, businessId = "") {
  const text = String(value || "").trim();
  const id = String(businessId || "").trim();
  if (!text) return "";
  if (id && text.toLowerCase() === id.toLowerCase()) return "";
  if (looksLikeFirestoreBusinessId(text)) return "";
  return text;
}

function businessNameFrom(data, fallback = null) {
  const businessId = String(data?.id || data?.businessId || fallback || "").trim();
  const candidates = [
    data?.businessName,
    data?.name,
    data?.title,
    data?.displayName,
    data?.publicName,
    data?.legalName,
    data?.profileName,
    data?.business?.name,
    data?.business?.businessName,
    fallback,
  ];

  for (const value of candidates) {
    const clean = cleanBusinessDisplayName(value, businessId);
    if (clean) return clean;
  }

  return null;
}

function employeeMatches(emp, uid, email) {
  const uidCandidates = uniqueTruthy([
    emp?.userUid,
    emp?.uid,
    emp?.userId,
    emp?.authUid,
    emp?.linkedUid,
    emp?.linkedUserId,
    emp?.linkedUserUid,
    emp?.firebaseUid,
    emp?.employeeUserUid,
    emp?.employeeUid,
    emp?.workerUid,
    emp?.workerUserUid,
  ]);

  const emailCandidates = uniqueTruthy([
    emp?.email,
    emp?.emailLower,
    emp?.emailNormalized,
    emp?.employeeEmail,
    emp?.employeeEmailLower,
    emp?.employeeEmailNormalized,
    emp?.workerEmail,
    emp?.workerEmailLower,
    emp?.workerEmailNormalized,
    emp?.inviteEmail,
    emp?.inviteEmailLower,
    emp?.inviteEmailNormalized,
    emp?.contactEmail,
    emp?.contactEmailLower,
    emp?.contactEmailNormalized,
    emp?.userEmail,
    emp?.userEmailLower,
    emp?.loginEmail,
    emp?.loginEmailLower,
    ...(Array.isArray(emp?.accessEmails) ? emp.accessEmails : []),
    ...(Array.isArray(emp?.allowedEmails) ? emp.allowedEmails : []),
  ]).map(norm);

  const matchedByUid = Boolean(uid && uidCandidates.includes(String(uid)));
  const matchedByEmail = Boolean(email && emailCandidates.includes(norm(email)));

  return {
    matched: matchedByUid || matchedByEmail,
    matchedByUid,
    matchedByEmail,
    preferredEmployeeId: emp?.id || emp?.employeeId || emp?.workerId || null,
    preferredEmail:
      emp?.email ||
      emp?.emailLower ||
      emp?.employeeEmail ||
      emp?.workerEmail ||
      emp?.inviteEmail ||
      emp?.contactEmail ||
      emp?.userEmail ||
      emp?.loginEmail ||
      null,
  };
}

function workerProfileFromRaw(raw = {}) {
  const source = raw && typeof raw === "object" ? raw : {};
  const name = source.workerName || source.employeeName || source.name || source.displayName || source.fullName || "";
  const photoUrl = source.workerPhotoUrl || source.employeePhotoUrl || source.photoUrl || source.photoURL || source.imageUrl || source.avatarUrl || "";
  return {
    workerName: name || "",
    employeeName: source.employeeName || name || "",
    workerPhotoUrl: photoUrl || "",
    employeePhotoUrl: source.employeePhotoUrl || photoUrl || "",
    availability: source.availability || source.employeeAvailability || source.workerAvailability || source.schedule || source.workingHours || source.hours || null,
    schedule: source.schedule || source.availability || null,
    workingHours: source.workingHours || source.availability || null,
    services: Array.isArray(source.services) ? source.services : [],
    serviceIds: Array.isArray(source.serviceIds) ? source.serviceIds : (Array.isArray(source.services) ? source.services : []),
    calendarSettings: source.calendarSettings || source.businessCalendarSettings || source.agendaSettings || null,
  };
}

function addOwnerBiz(resultsMap, bizId, bizName, raw = null) {
  if (!bizId) return;

  const key = `owner:${String(bizId)}`;
  resultsMap.set(key, {
    businessId: String(bizId),
    businessName: bizName || null,
    role: "owner",
    employeeId: null,
    employeeEmail: null,
    raw: raw || null,
  });
}

function addWorkerBiz(resultsMap, bizId, bizName, employeeId, employeeEmail, raw = null) {
  if (!bizId) return;

  // Keep owner and worker sections separate, but collapse all worker discovery
  // paths for the same business into one row. The same employee can be found
  // through root access arrays, mirror docs, collectionGroup and fallback scans.
  const key = `worker:${String(bizId)}`;
  const existing = resultsMap.get(key) || {};

  const rawProfile = workerProfileFromRaw(raw || {});
  const existingProfile = workerProfileFromRaw(existing.raw || existing || {});
  const mergedRaw = {
    ...(existing.raw && typeof existing.raw === "object" ? existing.raw : {}),
    ...(raw && typeof raw === "object" ? raw : {}),
  };

  resultsMap.set(key, {
    ...existing,
    businessId: String(bizId),
    businessName: bizName || existing.businessName || null,
    role: "worker",
    employeeId: employeeId || existing.employeeId || null,
    employeeEmail: employeeEmail || existing.employeeEmail || null,
    workerName: rawProfile.workerName || existing.workerName || existingProfile.workerName || "",
    employeeName: rawProfile.employeeName || existing.employeeName || existingProfile.employeeName || rawProfile.workerName || "",
    workerPhotoUrl: rawProfile.workerPhotoUrl || existing.workerPhotoUrl || existingProfile.workerPhotoUrl || "",
    employeePhotoUrl: rawProfile.employeePhotoUrl || existing.employeePhotoUrl || existingProfile.employeePhotoUrl || rawProfile.workerPhotoUrl || "",
    availability: rawProfile.availability || existing.availability || existingProfile.availability || null,
    schedule: rawProfile.schedule || existing.schedule || existingProfile.schedule || null,
    workingHours: rawProfile.workingHours || existing.workingHours || existingProfile.workingHours || null,
    services: rawProfile.services && rawProfile.services.length ? rawProfile.services : (existing.services || existingProfile.services || []),
    serviceIds: rawProfile.serviceIds && rawProfile.serviceIds.length ? rawProfile.serviceIds : (existing.serviceIds || existingProfile.serviceIds || []),
    calendarSettings: rawProfile.calendarSettings || existing.calendarSettings || existingProfile.calendarSettings || null,
    raw: mergedRaw,
  });
}

function businessIdFromEmployeeDoc(d) {
  try {
    const parent = d?.ref?.parent; // employees collection
    const businessDoc = parent?.parent; // businesses/{businessId}
    return businessDoc?.id || null;
  } catch (_) {
    return null;
  }
}

async function getBusinessLite(db, businessId) {
  try {
    if (!businessId) return null;
    const snap = await getDoc(doc(db, "businesses", businessId));
    if (snap && snap.exists()) return { id: snap.id, ...(snap.data() || {}) };
  } catch (_) {}
  return null;
}


async function addWorkersByBusinessAccessArrays({ db, resultsMap, uid, email }) {
  const bizCol = collection(db, "businesses");
  const checks = [];

  if (email) {
    checks.push(["workerAccessEmails", email]);
    checks.push(["workerEmails", email]);
    checks.push(["allowedWorkerEmails", email]);
  }

  if (uid) {
    checks.push(["workerAccessUids", uid]);
    checks.push(["workerUids", uid]);
    checks.push(["allowedWorkerUids", uid]);
  }

  for (const [field, value] of checks) {
    try {
      const snap = await getDocs(query(bizCol, where(field, "array-contains", value)));
      for (const d of snap.docs) {
        const data = d.data() || {};
        addWorkerBiz(
          resultsMap,
          d.id,
          businessNameFrom(data, d.id),
          null,
          email || null,
          { ...data, accessSource: field }
        );
      }
    } catch (e) {
      warnOnce(`worker-business-access-array:${field}:${e?.message || String(e)}`, "[userBusinesses] worker business access array query failed", { field, error: e?.message || String(e) });
    }
  }
}

async function addWorkersByMirrorDocs({ db, resultsMap, uid, email }) {
  if (email) {
    try {
      const snap = await getDocs(query(collectionGroup(db, "workerAccessEmails"), where("email", "==", email)));
      for (const d of snap.docs) {
        const data = { id: d.id, ...(d.data() || {}) };
        if (data.active === false) continue;
        const bizId = data.businessId || d?.ref?.parent?.parent?.id || null;
        if (!bizId) continue;
        const business = await getBusinessLite(db, bizId);
        addWorkerBiz(resultsMap, bizId, businessNameFrom(business, data.businessName || bizId), data.employeeId || null, data.email || email, { ...(business || {}), ...data, business: business || null });
      }
    } catch (e) {
      warnOnce(`workerAccessEmails:${e?.message || String(e)}`, "[userBusinesses] workerAccessEmails collectionGroup query failed", e?.message || String(e));
    }
  }

  if (uid) {
    try {
      const snap = await getDocs(query(collectionGroup(db, "workerAccess"), where("uid", "==", uid)));
      for (const d of snap.docs) {
        const data = { id: d.id, ...(d.data() || {}) };
        if (data.active === false) continue;
        const bizId = data.businessId || d?.ref?.parent?.parent?.id || null;
        if (!bizId) continue;
        const business = await getBusinessLite(db, bizId);
        addWorkerBiz(resultsMap, bizId, businessNameFrom(business, data.businessName || bizId), data.employeeId || null, data.email || null, { ...(business || {}), ...data, business: business || null });
      }
    } catch (e) {
      warnOnce(`workerAccess:${e?.message || String(e)}`, "[userBusinesses] workerAccess collectionGroup query failed", e?.message || String(e));
    }
  }
}

async function addWorkersByCollectionGroup({ db, resultsMap, uid, email }) {
  const employeeChecks = [];

  if (uid) {
    employeeChecks.push(["userUid", uid]);
    employeeChecks.push(["uid", uid]);
    employeeChecks.push(["userId", uid]);
    employeeChecks.push(["authUid", uid]);
    employeeChecks.push(["linkedUid", uid]);
    employeeChecks.push(["linkedUserId", uid]);
    employeeChecks.push(["linkedUserUid", uid]);
    employeeChecks.push(["firebaseUid", uid]);
    employeeChecks.push(["employeeUserUid", uid]);
    employeeChecks.push(["employeeUid", uid]);
    employeeChecks.push(["workerUid", uid]);
    employeeChecks.push(["workerUserUid", uid]);
  }

  if (email) {
    employeeChecks.push(["email", email]);
    employeeChecks.push(["emailLower", email]);
    employeeChecks.push(["emailNormalized", email]);
    employeeChecks.push(["employeeEmail", email]);
    employeeChecks.push(["employeeEmailLower", email]);
    employeeChecks.push(["employeeEmailNormalized", email]);
    employeeChecks.push(["workerEmail", email]);
    employeeChecks.push(["workerEmailLower", email]);
    employeeChecks.push(["workerEmailNormalized", email]);
    employeeChecks.push(["inviteEmail", email]);
    employeeChecks.push(["inviteEmailLower", email]);
    employeeChecks.push(["inviteEmailNormalized", email]);
    employeeChecks.push(["contactEmail", email]);
    employeeChecks.push(["contactEmailLower", email]);
    employeeChecks.push(["contactEmailNormalized", email]);
    employeeChecks.push(["userEmail", email]);
    employeeChecks.push(["userEmailLower", email]);
    employeeChecks.push(["loginEmail", email]);
    employeeChecks.push(["loginEmailLower", email]);
    employeeChecks.push(["accessEmails", email]);
    employeeChecks.push(["allowedEmails", email]);
  }

  const cg = collectionGroup(db, "employees");

  for (const [field, value] of employeeChecks) {
    try {
      const op = (field === "accessEmails" || field === "allowedEmails") ? "array-contains" : "==";
      const snap = await getDocs(query(cg, where(field, op, value)));

      for (const d of snap.docs) {
        const data = { id: d.id, ...(d.data() || {}) };
        const match = employeeMatches(data, uid, email);
        if (!match.matched) continue;

        const bizId =
          data.businessId ||
          data.businessID ||
          data.companyId ||
          businessIdFromEmployeeDoc(d);

        if (!bizId) continue;

        const business = await getBusinessLite(db, bizId);
        const bizName = businessNameFrom(business, data.businessName || data.companyName || bizId);

        addWorkerBiz(
          resultsMap,
          bizId,
          bizName,
          match.preferredEmployeeId || d.id,
          match.preferredEmail,
          { ...(business || {}), ...data, business: business || null }
        );
      }
    } catch (e) {
      // Rules/indexes may block one field. Continue with the next field.
      warnOnce(`employee-cg:${field}:${e?.message || String(e)}`, "[userBusinesses] employee collectionGroup query failed", { field, error: e?.message || String(e) });
    }
  }
}

async function addWorkersByBusinessScan({ db, resultsMap, uid, email }) {
  try {
    const bizSnap = await getDocs(collection(db, "businesses"));

    for (const b of bizSnap.docs) {
      const bizId = b.id;
      const bData = b.data() || {};
      const bizName = businessNameFrom(bData, bizId);

      try {
        const empSnap = await getDocs(collection(db, `businesses/${bizId}/employees`));

        for (const empDoc of empSnap.docs) {
          const emp = { id: empDoc.id, ...(empDoc.data() || {}) };
          const match = employeeMatches(emp, uid, email);

          if (match.matched) {
            addWorkerBiz(
              resultsMap,
              bizId,
              bizName,
              match.preferredEmployeeId || empDoc.id,
              match.preferredEmail,
              { ...bData, ...emp, business: bData }
            );
          }
        }
      } catch (e) {
        // Continue scanning other businesses.
        warnOnce(`employees-subcollection:${bizId}:${e?.message || String(e)}`, "[userBusinesses] employees subcollection scan failed", { bizId, error: e?.message || String(e) });
      }
    }
  } catch (e) {
    warnOnce(`business-scan:${e?.message || String(e)}`, "[userBusinesses] business scan failed", e?.message || String(e));
  }
}

async function addOwnersWeb({ db, resultsMap, uid, email }) {
  const bizCol = collection(db, "businesses");

  if (uid) {
    try {
      const snap = await getDocs(query(bizCol, where("ownerUid", "==", uid)));
      for (const d of snap.docs) {
        const data = d.data() || {};
        addOwnerBiz(resultsMap, d.id, businessNameFrom(data, d.id), data);
      }
    } catch (_) {}

    try {
      const snap = await getDocs(query(bizCol, where("admins", "array-contains", uid)));
      for (const d of snap.docs) {
        const data = d.data() || {};
        addOwnerBiz(resultsMap, d.id, businessNameFrom(data, d.id), data);
      }
    } catch (_) {}
  }

  if (email) {
    const emailFields = ["ownerEmail", "ownerEmailLower", "requestedByEmail", "businessEmail", "adminEmails"];
    for (const field of emailFields) {
      try {
        const op = field === "adminEmails" ? "array-contains" : "==";
        const snap = await getDocs(query(bizCol, where(field, op, email)));
        for (const d of snap.docs) {
          const data = d.data() || {};
          addOwnerBiz(resultsMap, d.id, businessNameFrom(data, d.id), data);
        }
      } catch (_) {}
    }
  }
}

async function addOwnersIos({ db, resultsMap, uid, email, bizList }) {
  for (const b of bizList || []) {
    const bizId = b?.id || b?.businessId || null;
    if (!bizId) continue;

    const ownerUid = b?.ownerUid ? String(b.ownerUid) : "";
    const ownerEmails = [b?.ownerEmail, b?.ownerEmailLower, b?.requestedByEmail, b?.businessEmail].map(norm).filter(Boolean);
    const adminEmails = Array.isArray(b?.adminEmails) ? b.adminEmails.map(norm) : [];

    const isOwner =
      (uid && ownerUid === uid) ||
      (email && (ownerEmails.includes(email) || adminEmails.includes(email))) ||
      hasUidInField(b?.admins, uid) ||
      hasUidInField(b?.managers, uid);

    if (isOwner) addOwnerBiz(resultsMap, bizId, businessNameFrom(b, bizId), b);
  }
}

function fieldHasNormalizedEmail(fieldValue, email) {
  if (!email || !fieldValue) return false;
  const target = norm(email);

  if (Array.isArray(fieldValue)) {
    return fieldValue.map(norm).includes(target);
  }

  if (typeof fieldValue === "object") {
    return Boolean(fieldValue[target] || fieldValue[email]);
  }

  return norm(fieldValue) === target;
}

async function addWorkersIosByBusinessAccessArrays({ resultsMap, uid, email, bizList }) {
  for (const b of bizList || []) {
    const bizId = b?.id || b?.businessId || null;
    if (!bizId) continue;

    const emailMatch = Boolean(email && [
      b?.workerAccessEmails,
      b?.workerEmails,
      b?.employeeEmails,
      b?.staffEmails,
      b?.accessEmails,
      b?.allowedEmails,
      b?.teamEmails,
    ].some((value) => fieldHasNormalizedEmail(value, email)));

    const uidMatch = Boolean(uid && [
      b?.workerAccessUids,
      b?.workerUids,
      b?.employeeUids,
      b?.staffUids,
      b?.accessUids,
      b?.allowedUids,
      b?.teamUids,
    ].some((value) => hasUidInField(value, uid)));

    if (emailMatch || uidMatch) {
      addWorkerBiz(
        resultsMap,
        bizId,
        businessNameFrom(b, bizId),
        null,
        email || null,
        b
      );
    }
  }
}

async function addWorkersIos({ db, resultsMap, uid, email, bizList, maxBusinessesToScan = 80 }) {
  const safeBizList = Array.isArray(bizList) ? bizList.slice(0, maxBusinessesToScan) : [];
  const concurrency = 6;
  let cursor = 0;

  async function scanOneBusiness(b) {
    const bizId = b?.id || b?.businessId || null;
    if (!bizId) return;

    const bizName = businessNameFrom(b, bizId);

    try {
      const employees = await withTimeout(
        fsClient.listCollectionByPath(`businesses/${bizId}/employees`, {}, { db }),
        2800,
        `ios-employees-timeout-${bizId}`
      );
      const arr = Array.isArray(employees) ? employees : [];

      for (const emp of arr) {
        const match = employeeMatches(emp, uid, email);

        if (match.matched) {
          addWorkerBiz(
            resultsMap,
            bizId,
            bizName,
            match.preferredEmployeeId || emp?.id || emp?.employeeId,
            match.preferredEmail,
            { ...(b || {}), ...(emp || {}), business: b }
          );
        }
      }
    } catch (e) {
      // On iOS we do not want one slow employee subcollection to block the entire app/header.
      warnOnce(`ios-employees-read:${bizId}:${e?.message || String(e)}`, "[userBusinesses] iOS employees read skipped", { bizId, error: e?.message || String(e) });
    }
  }

  async function worker() {
    while (cursor < safeBizList.length) {
      const idx = cursor;
      cursor += 1;
      // eslint-disable-next-line no-await-in-loop
      await scanOneBusiness(safeBizList[idx]);
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, safeBizList.length) }, worker));
}


const businessRequests = new WeakMap();
export function getUserBusinessesAndRoles(db = defaultDb, user, options = {}) {
  if (!db) return Promise.reject(new Error("db is required"));
  const identity = getCurrentIdentity(user);
  const key = JSON.stringify([identity.uid, identity.email, options.preferHttp !== false, options.cacheOnly === true]);
  let requests = businessRequests.get(db);
  if (!requests) { requests = new Map(); businessRequests.set(db, requests); }
  if (requests.has(key)) return requests.get(key);
  // Bound authentication and compatibility lookups too, not only the HTTP call.
  const request = withTimeout(loadUserBusinessesAndRoles(db, user, options), 12000, 'user-businesses-total-timeout')
    .finally(() => requests.delete(key));
  requests.set(key, request);
  return request;
}

async function loadUserBusinessesAndRoles(db = defaultDb, user, options = {}) {
  if (!db) throw new Error("getUserBusinessesAndRoles: db is required");

  const { uid, email } = getCurrentIdentity(user);
  if (!uid && !email) return [];

  const resultsMap = new Map();
  const cachedForUser = getCachedUserBusinesses(user);
  const allowCacheOnly = options && options.cacheOnly === true;

  if (allowCacheOnly && hasAnyRole(cachedForUser)) return cachedForUser;

  // V22: use the Admin SDK HTTP endpoint as the fast, single source of truth.
  // It returns owner + worker rows together, avoiding slow client-side Firestore scans
  // and the race where worker-only results overwrite owner businesses in the selector.
  if (options.preferHttp !== false) {
    try {
      const httpRows = await fetchUserBusinessesViaHttpV22(user, options, db);
      assertCurrentBusinessIdentity(user);
      setCachedUserBusinesses(user, httpRows);
      return httpRows;
    } catch (e) {
      warnOnce(`user-businesses-http-v22:${e?.message || String(e)}`, "[userBusinesses] HTTP listUserBusinesses fallback", e?.message || String(e));
      if (e?.message === "user-businesses-session-changed") throw e;
      // Recovery scans are opt-in diagnostics, never an automatic permission fallback.
      if (options.allowLegacyDiscovery !== true) throw e;
    }
  }

  if (isIosCapacitor()) {
    const cached = cachedForUser;
    const scanWorkers = options && options.scanWorkers !== false;

    try {
      const businesses = await withTimeout(
        fsClient.listCollectionByPath("businesses", {}, { db }),
        Number(options.iosBusinessTimeoutMs || 4500),
        "ios-businesses-timeout"
      );
      const bizList = Array.isArray(businesses) ? businesses : [];

      await addOwnersIos({ db, resultsMap, uid, email, bizList });

      // iOS fast worker path: business root arrays. This avoids depending only on
      // businesses/{id}/employees scans, which can be slower or blocked by rules in WKWebView.
      await addWorkersIosByBusinessAccessArrays({ resultsMap, uid, email, bizList });

      // Owners are the most important case for the calendar button. If found, return quickly
      // instead of scanning every employees subcollection on iOS startup.
      if (!scanWorkers && resultsMap.size > 0) {
        const ownerOut = Array.from(resultsMap.values())
          .map((item) => ({
            ...(item || {}),
            businessName: cleanBusinessDisplayName(item?.businessName, item?.businessId) || null,
          }))
          .sort((a, b) => String(a.businessName || "").localeCompare(String(b.businessName || "")));
        setCachedUserBusinesses(user, ownerOut);
        return ownerOut;
      }

      if (scanWorkers) {
        await withTimeout(
          addWorkersIos({
            db,
            resultsMap,
            uid,
            email,
            bizList,
            maxBusinessesToScan: Number(options.maxBusinessesToScan || 80),
          }),
          Number(options.iosWorkerScanTimeoutMs || 6500),
          "ios-workers-scan-timeout"
        ).catch((e) => {
          warnOnce(`ios-worker-scan:${e?.message || String(e)}`, "[userBusinesses] iOS worker scan timed out/skipped", e?.message || String(e));
        });
      }
    } catch (e) {
      warnOnce(`ios-load:${e?.message || String(e)}`, "[userBusinesses] iOS load failed", e?.message || String(e));
      if (hasAnyRole(cached)) return cached;
    }
  } else {
    await addOwnersWeb({ db, resultsMap, uid, email });

    // Fastest worker path: business root arrays written by BusinessEditPage/Workers.
    await addWorkersByBusinessAccessArrays({ db, resultsMap, uid, email });

    // Mirror docs path: businesses/{id}/workerAccessEmails and workerAccess.
    await addWorkersByMirrorDocs({ db, resultsMap, uid, email });

    // Fast path: collectionGroup employees by uid/email.
    await addWorkersByCollectionGroup({ db, resultsMap, uid, email });

    // Fallback: scan businesses/{id}/employees.
    // This catches cases where collectionGroup indexes/rules are not ready.
    await addWorkersByBusinessScan({ db, resultsMap, uid, email });
  }

  const out = Array.from(resultsMap.values())
    .map((item) => ({
      ...(item || {}),
      businessName: cleanBusinessDisplayName(item?.businessName, item?.businessId) || null,
    }))
    .sort((a, b) => {
      if (a.role === b.role) {
        return String(a.businessName || "").localeCompare(String(b.businessName || ""));
      }
      return a.role === "owner" ? -1 : 1;
    });

  setCachedUserBusinesses(user, out);
  return out;
}

export default getUserBusinessesAndRoles;
